import { Chain, Figure } from './primitives'

/* ============================ MERLION IT LEADERSHIP ============================ */

const LEADERS = [
  {
    name: 'Jesse Rovira',
    role: 'Founder & Technical Leader',
    line: 'Solution architecture for national-scale public platforms · Enterprise Asset Management · applied AI',
    alsoOnProject: 'Software Architect / Technical Lead on LAMS',
  },
  {
    name: 'Renato dos Santos Pereira',
    role: 'Chief Executive Officer',
    line: '25+ years in business management, team leadership, automation and business development · Brazil, U.S. and China',
  },
  {
    name: 'Marcos Mazieri',
    role: 'Chief Financial Officer',
    line: 'Ph.D. in innovation · business investor · research on project risk, budget deviation and fixed-price cost control',
  },
  {
    name: 'Samuel Lemos',
    role: 'Research & Laboratory Manager',
    line: 'Innovation and research · electrical and telecommunications engineering · applied AI, automation and cloud',
  },
  {
    name: 'Dylan Marlowe',
    role: 'Customer Experience & Sales Head',
    line: 'Market experience in AI-driven infrastructure inspection · product evolution, customer success, market analysis and positioning',
  },
]

export function LeadershipGrid() {
  return (
    <Figure label="Merlion IT corporate leadership: Jesse Rovira, Founder and Technical Leader, who is also the Software Architect and Technical Lead on LAMS; Renato dos Santos Pereira, Chief Executive Officer; Marcos Mazieri, Chief Financial Officer; and Samuel Lemos, Research and Laboratory Manager. Corporate leadership supports every Merlion IT engagement; the named LAMS key personnel are unchanged.">
      <div className="leaders">
        <ul className="leaders__grid">
          {LEADERS.map((l) => (
            <li key={l.name} className={`leaders__card${l.alsoOnProject ? ' leaders__card--project' : ''}`}>
              <span className="leaders__role">{l.role}</span>
              <span className="leaders__name">{l.name}</span>
              <span className="leaders__line">{l.line}</span>
              {l.alsoOnProject ? <span className="leaders__tag">Also on the project team · {l.alsoOnProject}</span> : null}
            </li>
          ))}
        </ul>
        <p className="leaders__note">
          Corporate leadership supports every Merlion IT engagement — conceptual, technical, advisory and supplementary
          support. It is not additional key personnel: the named LAMS team is unchanged.
        </p>
      </div>
    </Figure>
  )
}

/* ============================ PERSON PROFILES ============================ */

interface Profile {
  name: string
  role: string
  lead: string[]
  groups: { title: string; points: string[] }[]
}

function PersonProfile({ profile, label }: { profile: Profile; label: string }) {
  return (
    <Figure label={label}>
      <ul className="experts experts--quad">
        <li className="experts__card experts__card--lead">
          <span className="experts__role">{profile.role}</span>
          <span className="experts__name">{profile.name}</span>
          <ul className="experts__points">
            {profile.lead.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </li>
        {profile.groups.map((g) => (
          <li key={g.title} className="experts__card">
            <span className="experts__role">{g.title}</span>
            <ul className="experts__points">
              {g.points.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </Figure>
  )
}

const RENATO: Profile = {
  name: 'Renato dos Santos Pereira',
  role: 'Chief Executive Officer, Merlion IT',
  lead: [
    '25+ years in business management, team leadership, automation and business development',
    'Operations across Brazil, the United States and China; business in more than 50 countries',
    'Signed the submitted proposal documentation on behalf of Merlion IT LLC',
  ],
  groups: [
    {
      title: 'Technology and process transformation',
      points: [
        'Software, automation and AI initiatives for U.S. companies at Merlion IT, including the IzzyParcel real-estate platform',
        'CRM, systems integration, commercial automation and multichannel service with AI',
        'Process-integration projects with Zoho One, n8n and Twilio',
      ],
    },
    {
      title: 'Leadership',
      points: [
        'Atlas Copco, 1998–2010: led a structure of 80 technical and 20 administrative professionals',
        'President and co-founder, TV Connect USA',
        'Director of Complex Systems Architecture, Instituto Êxito · CVO, Singulys Holding',
      ],
    },
    {
      title: 'Education',
      points: [
        'Bachelor of Business Administration and Ethics — Cela International University',
        'Business Strategy with Artificial Intelligence — MIT',
        'Business Administration, emphasis in Marketing — Universidade Paulista',
      ],
    },
  ],
}

const MARCOS: Profile = {
  name: 'Marcos Mazieri',
  role: 'Chief Financial Officer, Merlion IT',
  lead: [
    'Ph.D. in Administration with a focus on Innovation — UNINOVE',
    'Business investor helping companies build, scale and become capital-market ready',
    'Former Professor, Post-Graduation Program in Business Management',
  ],
  groups: [
    {
      title: 'Market',
      points: [
        'Co-Founder & Business Investor, Houser — featured by Bloomberg Línea',
        'Infobankfor — AI-by-design strategy and finance analytics',
        'Preparation for institutional capital, M&A and IPO',
      ],
    },
    {
      title: 'Research record',
      points: [
        '46 publications · 526 citations · h-index 11 · 3 patents',
        'European Business Review, World Patent Information, Journal of Urban Technology',
        '65 thesis evaluation boards · 20 theses supervised',
      ],
    },
    {
      title: 'Own research relevant to LAMS',
      points: [
        'Budget deviation in Brazilian infrastructure projects',
        'The effect of project risk management on project success',
        'Cost maintenance in ERP implementations under fixed-price contracts',
      ],
    },
  ],
}

const SAMUEL: Profile = {
  name: 'Samuel Lemos',
  role: 'Research & Laboratory Manager, Merlion IT',
  lead: [
    'Leads innovation and research for Merlion IT engagements',
    'Electrical and telecommunications engineer — CEFET-RJ',
    'Master’s research in AI-based metaheuristics for system optimization and diagnosis — UFPR, in progress',
  ],
  groups: [
    {
      title: 'Education',
      points: [
        'Industrial Electrical Engineering, emphasis in Telecommunications — CEFET-RJ',
        'Telecommunications Engineering — CEFET-RJ',
        'Postgraduate: Systems Analysis (UniBF) · Renewable Energy (UNINTER)',
      ],
    },
    {
      title: 'Applied AI and data',
      points: [
        'Retrieval-augmented generation, LangChain and FAISS vector search',
        'TensorFlow, Keras and scikit-learn',
        'PostgreSQL and Redis; relational modeling, caching and event persistence',
      ],
    },
    {
      title: 'Platforms and automation',
      points: [
        'AWS, Docker, Nginx, Traefik and CI/CD',
        'n8n, webhooks, REST APIs and OAuth 2.0',
        'Telecommunications and voice AI · KNX automation · engineering for critical environments',
      ],
    },
  ],
}

const DYLAN: Profile = {
  name: 'Dylan Marlowe',
  role: 'Customer Experience & Sales Head, Merlion IT',
  lead: [
    'Hands-on experience with AI-driven inspection of wastewater and underground infrastructure',
    'Operated and maintained a fleet of autonomous inspection robots; NASSCO PACP / MACP / LACP certified, recertification pending',
    'Sales leadership, coaching and accountability against targets',
  ],
  groups: [
    {
      title: 'Product evolution',
      points: [
        'Operational needs seen in the field become product feedback',
        'Improvements prioritized with engineering, not just reported',
      ],
    },
    {
      title: 'Customer success',
      points: [
        'Validates that what the customer set out to achieve is what the product delivers',
        'Measured against the way the work is really done',
      ],
    },
    {
      title: 'Market analysis & positioning',
      points: [
        'Reads the market from the operational side — the problems customers have',
        'Translates AI, robotics and inspection capability into value a customer recognizes',
      ],
    },
  ],
}

export function ProfileRenato() {
  return (
    <PersonProfile
      profile={RENATO}
      label="Renato dos Santos Pereira, Chief Executive Officer of Merlion IT: more than 25 years in business management, team leadership, automation and business development; technology and process transformation; corporate leadership; and education."
    />
  )
}

export function ProfileMarcos() {
  return (
    <PersonProfile
      profile={MARCOS}
      label="Marcos Mazieri, Chief Financial Officer of Merlion IT: Ph.D. in Administration focused on Innovation, business investor, research record, and own research on budget deviation, project risk and fixed-price cost control."
    />
  )
}

export function ProfileSamuel() {
  return (
    <PersonProfile
      profile={SAMUEL}
      label="Samuel Lemos, Research and Laboratory Manager at Merlion IT: electrical and telecommunications engineer, master's research in AI-based optimization, with knowledge in applied AI and data, and in platforms and automation."
    />
  )
}

export function ProfileDylan() {
  return (
    <PersonProfile
      profile={DYLAN}
      label="Dylan Marlowe, Customer Experience and Sales Head at Merlion IT: market experience with AI-driven inspection of wastewater and underground infrastructure, and a role across product evolution, customer success validation, and market analysis and positioning."
    />
  )
}

/* ============================ TEAM ALIGNMENT ============================ */

const AREAS = [
  {
    area: 'Governance & Requirements',
    people: [
      { name: 'Felipe Nonato Marinho', role: 'Project Manager / Scrum Master', when: 'Project-long', named: true },
      { name: 'Requirements Analyst Sr.', role: 'Senior specialist', when: 'Discovery through UAT', named: false },
    ],
  },
  {
    area: 'Architecture & Engineering',
    people: [
      { name: 'Jesse Rovira', role: 'Software Architect / Technical Lead', when: 'Project-long', named: true },
      { name: 'Pedro Bucci', role: 'Fullstack Developer Sr.', when: 'Primary development lifecycle', named: true },
      { name: 'Infrastructure Analyst Sr.', role: 'Senior specialist', when: 'Foundation, releases and go-live', named: false },
    ],
  },
  {
    area: 'GIS, Data & Integration',
    people: [
      { name: 'Carlos Ribeiro', role: 'GIS Analyst Sr.', when: 'Discovery through system validation', named: true },
      { name: 'Data Migration / ETL Specialist Sr.', role: 'Senior specialist', when: 'Discovery through migration acceptance', named: false },
    ],
  },
  {
    area: 'Quality, Experience & Readiness',
    people: [
      { name: 'Bruno Freitas', role: 'UX/UI Designer Sr.', when: 'Discovery and functional delivery', named: true },
      { name: 'QA / Test Engineer Sr.', role: 'Senior specialist', when: 'Development through go-live', named: false },
      { name: 'Training / Documentation Specialist', role: 'Senior specialist', when: 'Progressive documentation and final readiness', named: false },
    ],
  },
]

export function TeamAlignment() {
  return (
    <Figure
      label="Four responsibility areas with their roles and planned involvement. Governance and Requirements: Felipe Nonato Marinho, project-long, and a Requirements Analyst from discovery through UAT. Architecture and Engineering: Jesse Rovira, project-long; Pedro Bucci for the primary development lifecycle; an Infrastructure Analyst for foundation, releases and go-live. GIS, Data and Integration: Carlos Ribeiro from discovery through system validation, and a Data Migration Specialist from discovery through migration acceptance. Quality, Experience and Readiness: Bruno Freitas for discovery and functional delivery, a QA Engineer from development through go-live, and a Training Specialist for progressive documentation and final readiness."
      wide
    >
      <ul className="align">
        {AREAS.map((a) => (
          <li key={a.area} className="align__area">
            <span className="align__title">{a.area}</span>
            <ul className="align__people">
              {a.people.map((p) => (
                <li key={p.name} className={`align__person${p.named ? ' is-named' : ''}`}>
                  <span className="align__name">{p.name}</span>
                  <span className="align__role">{p.named ? p.role : 'Senior specialist role'}</span>
                  <span className="align__when">{p.when}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </Figure>
  )
}

/* ============================ EAM CONCEPT ============================ */

export function StrategicProperty() {
  return (
    <Figure
      label="From asset to Strategic Public Asset. An asset has potential or actual value to an organization, per ISO 55000. It is a strategic asset when it is valuable, scarce and hard to substitute. It is a Strategic Public Asset when it is necessary to the public mission, legitimacy and long-term public value. In LAMS, this managed entity is the Property, managed to preserve or increase its capacity to generate legitimate public outcomes."
      wide
    >
      <div className="strategic">
        <Chain
          tone="land"
          emphasis={2}
          steps={[
            { label: 'Asset', detail: 'Potential or actual value to an organization — ISO 55000' },
            { label: 'Strategic Asset', detail: 'Valuable, scarce, hard to substitute — its loss affects priority objectives' },
            { label: 'Strategic Public Asset', detail: 'Necessary to the public mission, legitimacy and long-term public value' },
          ]}
        />
        <div className="strategic__card">
          <span className="strategic__eyebrow">In LAMS</span>
          <span className="strategic__title">This managed entity is the Property</span>
          <span className="strategic__line">
            Managed to preserve or increase its capacity to generate legitimate public outcomes — not only to preserve its
            physical condition.
          </span>
        </div>
      </div>
    </Figure>
  )
}

const EAM_QUESTIONS = [
  'What Properties are under the District’s responsibility?',
  'Why is each one strategically important?',
  'What is its condition?',
  'What risks does it present?',
  'What interventions are required?',
  'Who is accountable?',
  'What was spent?',
  'What result was achieved?',
]

export function EamQuestions() {
  return (
    <Figure
      label="The eight questions EAM answers: what Properties are under the District's responsibility, why each is strategically important, its condition, its risks, the interventions required, who is accountable, what was spent, and what result was achieved. Three governance levels use the same record: strategic, for Governing Board and Lands Committee decisions; tactical, for three-year plans and LAS evaluation; operational, for inspections, maintenance, natural-resource and timber work."
      wide
    >
      <div className="eamq">
        <ol className="eamq__list">
          {EAM_QUESTIONS.map((q, i) => (
            <li key={q} className="eamq__item">
              <span className="eamq__n mono">{String(i + 1).padStart(2, '0')}</span>
              <span className="eamq__q">{q}</span>
            </li>
          ))}
        </ol>
        <ul className="eamq__levels">
          <li className="eamq__level">
            <span className="eamq__level-name">Strategic</span>
            <span className="eamq__level-detail">Governing Board and Lands Committee decisions and priorities</span>
          </li>
          <li className="eamq__level">
            <span className="eamq__level-name">Tactical</span>
            <span className="eamq__level-detail">Three-year plans, program priorities and LAS evaluation</span>
          </li>
          <li className="eamq__level">
            <span className="eamq__level-name">Operational</span>
            <span className="eamq__level-detail">Inspections, maintenance, prescribed fire, timber and monitoring work</span>
          </li>
        </ul>
      </div>
    </Figure>
  )
}

const EAM_KEEP = ['Lifecycle and history', 'Condition and risk', 'Work, inspections and interventions', 'Costs and contracts']

const EAM_EXTEND = [
  { need: 'Geospatial identity', lams: 'GIS-native Properties; ArcGIS stays authoritative for geometry' },
  { need: 'Ecological condition', lams: 'Measures with time-stamped, sourced readings' },
  { need: 'Legal interests', lams: 'Ownership and lease-agreement context in the land record' },
  { need: 'Public authorization', lams: 'LAS, Lands Committee and Board approvals as governed workflow' },
  { need: 'Accountability', lams: 'Complete history, audit and approval traceability' },
  { need: 'Sensitive information', lams: 'Role-based redaction of rare-species and archaeological data' },
]

export function EamExtended() {
  return (
    <Figure
      label={`Conventional EAM is kept for ${EAM_KEEP.join(', ')}. Public land extends it, and LAMS carries each extension: ${EAM_EXTEND.map((e) => `${e.need}: ${e.lams}`).join('; ')}.`}
      wide
    >
      <div className="eamx">
        <div className="eamx__col eamx__col--keep">
          <span className="eamx__head">Conventional EAM — keep</span>
          <ul className="eamx__keep">
            {EAM_KEEP.map((k) => (
              <li key={k}>{k}</li>
            ))}
          </ul>
        </div>
        <div className="eamx__col eamx__col--extend">
          <span className="eamx__head">Public land — extend, and how LAMS carries it</span>
          <ul className="eamx__extend">
            {EAM_EXTEND.map((e) => (
              <li key={e.need}>
                <span className="eamx__need">{e.need}</span>
                <span className="eamx__lams">{e.lams}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Figure>
  )
}

/* ============================ DISCIPLINES ============================ */

const DISCIPLINES = [
  {
    name: 'Land Administration',
    ref: 'ISO 19152 · cadastre',
    good: 'Ownership, easements, leases and legal boundaries',
    gap: 'Condition, work, cost and operational history',
  },
  {
    name: 'Infrastructure & Public Asset Management',
    ref: 'ISO 55000 · IIMM',
    good: 'Portfolio policy, levels of service, long-term cost',
    gap: 'The operating model at the level of each entity',
  },
  {
    name: 'Natural Resource & Adaptive Management',
    ref: '',
    good: 'Ecological condition, monitoring, cycles of adjustment',
    gap: 'Work, contracts, approvals and cost',
  },
  {
    name: 'Natural Capital Accounting',
    ref: 'SEEA · ecosystem services',
    good: 'Environmental, social and cultural value',
    gap: 'Day-to-day operation — it is a lens for valuation',
  },
  {
    name: 'Real Estate / Portfolio Management',
    ref: '',
    good: 'Acquisition, disposition, leases, market value',
    gap: 'Custody of the same entity across decades',
  },
  {
    name: 'Public asset accounting',
    ref: 'GASB 34',
    good: 'The financial record and public accountability',
    gap: 'What condition the land is in, and what to do about it',
  },
]

export function DisciplinesCompare() {
  return (
    <Figure
      label={`Six disciplines could organize public land. ${DISCIPLINES.map((d) => `${d.name}${d.ref ? ` (${d.ref})` : ''} organizes ${d.good}, but does not answer ${d.gap}`).join('. ')}. EAM descends to each Property — condition, risk, work, cost, accountability and result — from acquisition through disposition, including maintenance, improvement and evolution. The others enter LAMS as extensions.`}
      wide
    >
      <div className="disc">
        <div className="table-wrap">
          <table className="disc__table">
            <thead>
              <tr>
                <th scope="col">Discipline</th>
                <th scope="col">Organizes well</th>
                <th scope="col">Does not answer</th>
              </tr>
            </thead>
            <tbody>
              {DISCIPLINES.map((d) => (
                <tr key={d.name}>
                  <th scope="row">
                    {d.name}
                    {d.ref ? <span className="disc__ref">{d.ref}</span> : null}
                  </th>
                  <td>{d.good}</td>
                  <td>{d.gap}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="disc__close">
          <span className="disc__close-title">EAM descends to each Property</span>
          <span className="disc__chain">
            condition <b>→</b> risk <b>→</b> work <b>→</b> cost <b>→</b> accountability <b>→</b> result
          </span>
          <span className="disc__close-note">
            Acquisition through disposition, including maintenance, improvement and evolution. The other disciplines
            enter as extensions.
          </span>
        </div>
      </div>
    </Figure>
  )
}

/* ============================ PROPERTY EXAMPLE ============================ */

const CHILDREN = [
  {
    name: 'Lake',
    cls: 'Water Body › Lake',
    attributes: 'Surface area · public access',
    measures: 'Water level · water quality · algae presence',
  },
  {
    name: 'Forest stand',
    cls: 'Natural Community › Managed Forest Stand',
    attributes: 'Species composition · fire-dependent',
    measures: 'Timber volume · canopy cover · invasive-plant cover',
  },
  {
    name: 'River',
    cls: 'Water Body › River · linear',
    attributes: 'Length within the Property',
    measures: 'Per segment: water level · bank condition · erosion',
    segments: ['Segment 1', 'Segment 2', 'Segment 3'],
  },
]

export function PropertyExample() {
  return (
    <Figure
      label="Illustrative example, not District data. A Land Property classified as Conservation Land, with attributes acreage, county and management region, and measures land-cover change and erosion condition, contains three related Properties: a Lake, with water level, water quality and algae presence measured over time; a Forest stand, with timber volume, canopy cover and invasive-plant cover; and a River managed as three segments, each with water level, bank condition and erosion readings."
      wide
    >
      <div className="propx">
        <div className="propx__root">
          <span className="propx__name">Land</span>
          <span className="propx__cls">Conservation Land</span>
          <span className="propx__row">
            <b>Attributes</b> Acreage · county · management region · ownership and lease context
          </span>
          <span className="propx__row">
            <b>Measures</b> Land-cover change · erosion condition
          </span>
        </div>
        <ul className="propx__children">
          {CHILDREN.map((c) => (
            <li key={c.name} className="propx__child">
              <span className="propx__name">{c.name}</span>
              <span className="propx__cls">{c.cls}</span>
              <span className="propx__row">
                <b>Attributes</b> {c.attributes}
              </span>
              <span className="propx__row">
                <b>Measures over time</b> {c.measures}
              </span>
              {c.segments ? (
                <span className="propx__segments">
                  {c.segments.map((s) => (
                    <span key={s} className="propx__segment">
                      {s}
                    </span>
                  ))}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
        <p className="propx__caption">Illustrative example — not District data</p>
      </div>
    </Figure>
  )
}

/* ============================ ARCHITECTURE AT A GLANCE ============================ */

const GLANCE = [
  { name: 'Objects', what: 'Governed business objects with composable capabilities', delivers: 'New needs configured on the model, not rebuilt screen by screen' },
  { name: 'Event layer', what: 'Change and event in one SQL Server transaction; workers with retries', delivers: 'Integrations and automations are never silently lost' },
  { name: 'Workflow', what: 'State machines, guards, approvals and execution pipelines', delivers: 'LAS, Committee and Board approvals run as governed flows' },
  { name: 'AI by design', what: 'Governed semantic context; local Gemma inference via Ollama', delivers: 'Assistive answers; District data stays in District infrastructure' },
  { name: 'Security by design', what: 'Entra ID SSO, RBAC, row-level security, field policy', delivers: 'Restricted data stays restricted on every access path' },
  { name: 'GIS', what: 'GeoEnabled objects, SQL Server spatial, ArcGIS provider', delivers: 'Triage, planning and evaluation with geographic context' },
]

export function ArchitectureGlance() {
  return (
    <Figure
      label={`Six architectural elements and what each delivers: ${GLANCE.map((g) => `${g.name} — ${g.what}; delivers: ${g.delivers}`).join('. ')}.`}
      wide
    >
      <ul className="glance">
        {GLANCE.map((g) => (
          <li key={g.name} className="glance__card">
            <span className="glance__name">{g.name}</span>
            <span className="glance__what">{g.what}</span>
            <span className="glance__delivers">{g.delivers}</span>
          </li>
        ))}
      </ul>
    </Figure>
  )
}

/* ============================ PRODUCTS ============================ */

function ProductModules({
  product,
  role,
  modules,
  tone,
  label,
}: {
  product: string
  role: string
  modules: { name: string; supports: string }[]
  tone: 'river' | 'ink'
  label: string
}) {
  return (
    <Figure label={label} wide>
      <div className={`prodmod prodmod--${tone}`}>
        <div className="prodmod__head">
          <span className="prodmod__product">{product}</span>
          <span className="prodmod__role">{role}</span>
        </div>
        <ul className="prodmod__grid">
          {modules.map((m) => (
            <li key={m.name} className="prodmod__module">
              <span className="prodmod__name">{m.name}</span>
              <span className="prodmod__supports">{m.supports}</span>
            </li>
          ))}
        </ul>
      </div>
    </Figure>
  )
}

const STUDIO_MODULES = [
  { name: 'Object Designer', supports: 'Unified Land Record — Property, parcel, tract, cases' },
  { name: 'Workflow / Automation', supports: 'Approvals, task assignment and notifications' },
  { name: 'Application Designer', supports: 'One unified, responsive web workspace' },
  { name: 'Security Studio', supports: 'Role-based access and redaction of restricted data' },
  { name: 'Integration Registry', supports: 'ArcGIS, CivicPlus, PaperVision, AccuFund, PERCH' },
  { name: 'AI Profile', supports: 'AI Information Assistant under the same access control' },
  { name: 'Package & Publishing', supports: 'Validated promotion: Development → UAT → Production' },
]

const RUNTIME_MODULES = [
  { name: 'Object services', supports: 'One land record across acquisition, management and disposition' },
  { name: 'State & action runtime', supports: 'LAS, Lands Committee and Governing Board approvals' },
  { name: 'Event & job runtime', supports: 'Reliable integrations, notifications and document generation' },
  { name: 'Policy resolver', supports: 'Redaction enforced on screens, reports, exports, GIS and AI' },
  { name: 'Query & UI runtime', supports: 'Dashboards, reports and workspace showing only permitted data' },
  { name: 'GIS engine', supports: 'Maps, triage and planning inside the workflow' },
  { name: 'AI semantic services', supports: 'Local, permission-aware AI assistance' },
]

export function ProductStudio() {
  return (
    <ProductModules
      product="LAMS Studio"
      role="Defines the application as governed metadata"
      tone="river"
      modules={STUDIO_MODULES}
      label={`LAMS Studio modules and the demand each supports: ${STUDIO_MODULES.map((m) => `${m.name} — ${m.supports}`).join('; ')}.`}
    />
  )
}

export function ProductRuntime() {
  return (
    <ProductModules
      product="LAMS Runtime"
      role="Interprets and enforces what Studio defines"
      tone="ink"
      modules={RUNTIME_MODULES}
      label={`LAMS Runtime modules and the demand each supports: ${RUNTIME_MODULES.map((m) => `${m.name} — ${m.supports}`).join('; ')}.`}
    />
  )
}

const OPS_DOMAINS = [
  {
    name: 'Land Acquisition',
    backlog: 'LA-01 – LA-08 · M3',
    items: ['Intake & file', 'GIS triage', 'Prospectus', 'LAS ranking', 'Committee & Board', 'Due diligence', 'Closing'],
  },
  {
    name: 'Land Management',
    backlog: 'LM-01 – LM-08 · M4',
    items: ['Three-year planning', 'GIS program planning', 'Work scheduling', 'Maintenance', 'Natural resources', 'Timber', 'Contracts & assets'],
  },
  {
    name: 'Land Disposition',
    backlog: 'LD-01 – LD-06 · M5',
    items: ['Initiation', 'GIS evaluation', 'LAS review', 'Committee & Board', 'Due diligence', 'Closing'],
  },
]

const OPS_SHARED = ['Property workspace', 'My Work', 'GIS workspace', 'Documents', 'Dashboards & reports', 'Timeline & notifications', 'AI assistant']
const OPS_INTEGRATIONS = ['ArcGIS', 'CivicPlus', 'PaperVision', 'AccuFund', 'PERCH', 'Entra ID']

export function ProductOperations() {
  return (
    <Figure
      label={`LAMS Operations. Three business domains: ${OPS_DOMAINS.map((d) => `${d.name} (${d.backlog}): ${d.items.join(', ')}`).join('; ')}. Shared applications: ${OPS_SHARED.join(', ')}. All over one Unified Land Record, integrated with ${OPS_INTEGRATIONS.join(', ')}.`}
      wide
    >
      <div className="opsmod">
        <ul className="opsmod__domains">
          {OPS_DOMAINS.map((d) => (
            <li key={d.name} className="opsmod__domain">
              <span className="opsmod__name">{d.name}</span>
              <span className="opsmod__backlog mono">{d.backlog}</span>
              <ul className="opsmod__items">
                {d.items.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
        <div className="opsmod__band opsmod__band--shared">
          <span className="opsmod__band-title">Shared applications · Core Platform · M2</span>
          <ul className="opsmod__chips">
            {OPS_SHARED.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
        <div className="opsmod__band opsmod__band--record">
          <span className="opsmod__band-title">Unified Land Record</span>
        </div>
        <div className="opsmod__band opsmod__band--integrations">
          <span className="opsmod__band-title">Integrations · M3–M6</span>
          <ul className="opsmod__chips">
            {OPS_INTEGRATIONS.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
      </div>
    </Figure>
  )
}

/* ============================ INTEGRATION ============================ */

const SYSTEMS_LEFT = [
  { name: 'ArcGIS Server / Online', how: 'ESRI APIs / SDK', role: 'Maps, triage and planning context · geometry stays in ArcGIS' },
  { name: 'CivicPlus', how: 'API', role: 'Applications into acquisition intake' },
  { name: 'PaperVision Enterprise', how: 'API', role: 'Search and link documents · stays the repository' },
  { name: 'Field Maps / Survey123', how: 'Existing field apps', role: 'Remain in place · field data integrated' },
]

const SYSTEMS_RIGHT = [
  { name: 'AccuFund', how: 'Scheduled CSV or supported mechanism', role: 'Financial context' },
  { name: 'PERCH', how: 'District SQL Server views', role: 'Read-only project data' },
  { name: 'Microsoft Entra ID / 365', how: 'Microsoft identity', role: 'Single sign-on and identity' },
]

function SystemCard({ s }: { s: { name: string; how: string; role: string } }) {
  return (
    <li className="integ__system">
      <span className="integ__name">{s.name}</span>
      <span className="integ__how">{s.how}</span>
      <span className="integ__role">{s.role}</span>
    </li>
  )
}

export function IntegrationMap() {
  const all = [...SYSTEMS_LEFT, ...SYSTEMS_RIGHT]
  return (
    <Figure
      label={`LAMS integration layer — one adapter per system, exchanges run through the event layer with monitoring, retries and reprocessing. Systems: ${all.map((s) => `${s.name} via ${s.how}: ${s.role}`).join('; ')}.`}
      wide
    >
      <div className="integ">
        <ul className="integ__side">
          {SYSTEMS_LEFT.map((s) => (
            <SystemCard key={s.name} s={s} />
          ))}
        </ul>
        <div className="integ__core">
          <span className="integ__core-title">LAMS</span>
          <ul className="integ__core-layers">
            <li>Adapter per system</li>
            <li>Event layer · retries · idempotency</li>
            <li>Monitoring · traceable errors · reprocessing</li>
          </ul>
          <span className="integ__core-note">Business logic never depends on an external interface</span>
        </div>
        <ul className="integ__side">
          {SYSTEMS_RIGHT.map((s) => (
            <SystemCard key={s.name} s={s} />
          ))}
        </ul>
      </div>
    </Figure>
  )
}

/* ============================ SCRUM ============================ */

export function ScrumCycle() {
  return (
    <Figure
      label="Scrum framework: accountabilities — Product Owner, Scrum Master, Developers; events — Sprint, Sprint Planning, Daily Scrum, Sprint Review, Sprint Retrospective; artifacts — Product Backlog, Sprint Backlog, Increment. In LAMS each two-week sprint runs refinement, design, implementation, testing, demonstration and feedback. Sprint 0 plus Sprints 1 to 16; a demonstration every sprint; formal acceptance at milestones M1 to M9."
      wide
    >
      <div className="scrum">
        <ul className="scrum__framework">
          <li className="scrum__col">
            <span className="scrum__col-title">Accountabilities</span>
            <span className="scrum__col-items">Product Owner · Scrum Master · Developers</span>
          </li>
          <li className="scrum__col">
            <span className="scrum__col-title">Events</span>
            <span className="scrum__col-items">Sprint · Planning · Daily Scrum · Review · Retrospective</span>
          </li>
          <li className="scrum__col">
            <span className="scrum__col-title">Artifacts</span>
            <span className="scrum__col-items">Product Backlog · Sprint Backlog · Increment</span>
          </li>
        </ul>
        <span className="scrum__label">Every LAMS sprint — two weeks</span>
        <Chain
          steps={[
            { label: 'Refinement' },
            { label: 'Design' },
            { label: 'Build' },
            { label: 'Test' },
            { label: 'Demo' },
            { label: 'Feedback' },
          ]}
        />
        <ul className="scrum__facts">
          <li>
            <b>Sprint 0 + 16 sprints</b> Baseline confirmed before kickoff
          </li>
          <li>
            <b>Product Backlog</b> The contracted scope baseline
          </li>
          <li>
            <b>Demo every sprint</b> The District sees working software
          </li>
          <li>
            <b>M1–M9</b> Formal acceptance, then payment
          </li>
        </ul>
      </div>
    </Figure>
  )
}

/* ============================ OUTCOMES ============================ */

const OUTCOMES = [
  { outcome: 'Reduced manual effort', kpi: 'Supports the ~40% objective; baseline set in Sprint 0' },
  { outcome: 'Data reliability', kpi: 'Validation rules; exceptions tracked and resolved' },
  { outcome: 'Migration accuracy', kpi: 'Source-to-target reconciliation with District sign-off' },
  { outcome: 'Process automation', kpi: 'Agreed workflow steps supported by LAMS' },
  { outcome: 'Release quality', kpi: 'Acceptance results against release commitments' },
  { outcome: 'Integration reliability', kpi: 'Interfaces monitored; failures traceable' },
  { outcome: 'User readiness', kpi: 'Training completed before readiness approval' },
  { outcome: 'Operational adoption', kpi: 'Core workflows in use during hypercare' },
  { outcome: 'Production stability', kpi: 'Critical go-live issues resolved before acceptance' },
  { outcome: 'Continuous improvement', kpi: 'Improvements governed through backlog and release control' },
]

export function OutcomesGrid() {
  return (
    <Figure
      label={`Ten outcomes and how each is measured: ${OUTCOMES.map((o) => `${o.outcome} — ${o.kpi}`).join('; ')}.`}
      wide
    >
      <ul className="outcomes">
        {OUTCOMES.map((o) => (
          <li key={o.outcome} className="outcomes__item">
            <span className="outcomes__name">{o.outcome}</span>
            <span className="outcomes__kpi">{o.kpi}</span>
          </li>
        ))}
      </ul>
    </Figure>
  )
}

/* ============================ SUPPORT ============================ */

const CHANNELS = [
  { sev: 'S1 Critical', portal: 'Ticket required', phone: 'Available and recommended' },
  { sev: 'S2 High', portal: 'Ticket required', phone: 'When urgent at opening' },
  { sev: 'S3 Medium', portal: 'Ticket required', phone: 'Not an initial channel' },
  { sev: 'S4 Low / Request', portal: 'Ticket required', phone: 'Not applicable' },
]

export function SupportModel() {
  return (
    <Figure
      label={`Technical support model. Level 1, service desk and functional support: registration, severity classification, diagnosis, user guidance and escalation. Level 2, technical and application support: troubleshooting, integration analysis, defect correction and corrective releases. Channels by severity: ${CHANNELS.map((c) => `${c.sev} — portal: ${c.portal}; telephone: ${c.phone}`).join('; ')}. Twelve months from final acceptance, Monday to Friday 8 a.m. to 5 p.m. Eastern Time; every request is a ticket; SLA reporting.`}
      wide
    >
      <div className="support">
        <ul className="support__levels">
          <li className="support__level">
            <span className="support__level-name">Level 1 · Service desk</span>
            <span className="support__level-detail">Registration, severity classification, diagnosis, user guidance, escalation</span>
          </li>
          <li className="support__level">
            <span className="support__level-name">Level 2 · Technical & application</span>
            <span className="support__level-detail">Troubleshooting, integration analysis, defect correction, corrective releases</span>
          </li>
        </ul>
        <div className="table-wrap">
          <table className="support__table">
            <thead>
              <tr>
                <th scope="col">Severity</th>
                <th scope="col">Support Portal</th>
                <th scope="col">Telephone</th>
              </tr>
            </thead>
            <tbody>
              {CHANNELS.map((c) => (
                <tr key={c.sev}>
                  <th scope="row">{c.sev}</th>
                  <td>{c.portal}</td>
                  <td>{c.phone}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="support__strip">12 months from final acceptance · M–F 8:00 a.m.–5:00 p.m. ET · every request is a ticket · SLA reporting</p>
      </div>
    </Figure>
  )
}

const SLA = [
  { sev: 'S1 Critical', response: '30 minutes', restore: '4 business hours', resolve: 'Continuous effort through restoration' },
  { sev: 'S2 High', response: '1 business hour', restore: '8 business hours', resolve: '2 business days' },
  { sev: 'S3 Medium', response: '4 business hours', restore: 'As applicable', resolve: '3 business days' },
  { sev: 'S4 Low / Request', response: '1 business day', restore: 'Not applicable', resolve: '5 business days' },
]

export function SeveritySla() {
  return (
    <Figure
      label={`Service targets by severity: ${SLA.map((s) => `${s.sev} — initial response ${s.response}, restoration or workaround ${s.restore}, resolution ${s.resolve}`).join('; ')}.`}
      wide
    >
      <div className="table-wrap">
        <table className="sla">
          <thead>
            <tr>
              <th scope="col">Severity</th>
              <th scope="col">Initial response</th>
              <th scope="col">Restoration / workaround</th>
              <th scope="col">Resolution</th>
            </tr>
          </thead>
          <tbody>
            {SLA.map((s) => (
              <tr key={s.sev} className={s.sev.startsWith('S1') ? 'sla__row--critical' : undefined}>
                <th scope="row">{s.sev}</th>
                <td>{s.response}</td>
                <td>{s.restore}</td>
                <td>{s.resolve}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Figure>
  )
}

const SUPPORT_INCLUDED = [
  'Service desk and ticketing',
  'Corrective maintenance',
  'Application and integration support',
  'Application monitoring',
  'Security and dependency maintenance',
  'SLA reporting',
]

export function SupportInvestment() {
  return (
    <Figure
      label={`Technical support investment: US$40,000 for a twelve-month agreement, Level 1 and Level 2, Monday to Friday 8 a.m. to 5 p.m. Eastern Time. Included: ${SUPPORT_INCLUDED.join(', ')}. Not included: new functional development, by change request; extended or after-hours coverage, optional and separately priced. Together with the US$610,000 implementation price, US$650,000 in total.`}
      wide
    >
      <div className="supinv">
        <div className="supinv__price">
          <span className="supinv__label">Technical Support · 12 months</span>
          <span className="supinv__figure">US$ 40,000</span>
          <span className="supinv__note">Level 1 + Level 2 · M–F 8:00 a.m.–5:00 p.m. ET · separate from the implementation price</span>
        </div>
        <div className="supinv__lists">
          <div>
            <span className="supinv__list-title">Included</span>
            <ul className="supinv__list">
              {SUPPORT_INCLUDED.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
          <div>
            <span className="supinv__list-title">Not included</span>
            <ul className="supinv__list supinv__list--out">
              <li>New functional development — change request</li>
              <li>Extended or after-hours coverage — optional, priced separately</li>
            </ul>
          </div>
        </div>
        <p className="supinv__total">Implementation US$ 610,000 + support US$ 40,000 = <b>US$ 650,000</b>, as on Form A</p>
      </div>
    </Figure>
  )
}
